import {
  MissionAssessmentRequestCreateDto,
  MissionAssessmentRequestSelectDto,
  MissionAssessmentRequestsSelectDto,
  TechnologySelectDto,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryKeys } from './keys';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from './users.query';

export type GetMissionAssessmentRequestsFilters = {
  technologyId?: string;
  status?: string;
};

const updateTechnologyMissionAssessmentRequestsCache = (params: {
  missionAssessmentRequest: MissionAssessmentRequestSelectDto;
  queryClient: ReturnType<typeof useQueryClient>;
}): void => {
  const { missionAssessmentRequest, queryClient } = params;

  queryClient.setQueryData<TechnologySelectDto | undefined>(
    queryKeys.technology.getTechnology(missionAssessmentRequest.technology.id),
    (cachedTechnology) => {
      if (!cachedTechnology) {
        return cachedTechnology;
      }

      const nextRequests = [...(cachedTechnology.missionAssessmentRequests ?? [])];
      const existingRequestIndex = nextRequests.findIndex((request) => request.id === missionAssessmentRequest.id);

      if (existingRequestIndex >= 0) {
        nextRequests[existingRequestIndex] = missionAssessmentRequest;
      } else {
        nextRequests.unshift(missionAssessmentRequest);
      }

      return {
        ...cachedTechnology,
        missionAssessmentRequests: nextRequests,
      };
    },
  );
};

export const useGetLatestMissionAssessmentRequestForTechnology = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = isAuthenticated && technologyId !== undefined && selectedCompanyId !== undefined;

  return useQuery<MissionAssessmentRequestSelectDto | null, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!technologyId) {
        throw new Error('technologyId is required to fetch latest mission assessment request.');
      }
      const result = await apiGet<MissionAssessmentRequestSelectDto>({
        path: `/v1/innovator/mission-assessment-requests/technology/${technologyId}`,
      });
      return result || null;
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentRequests.getLatestMissionAssessmentRequestForTechnology(technologyId)
      : queryKeys.placeholders.default,
  });
};

export const useGetMissionAssessmentRequest = (requestId?: string) => {
  const { apiGet } = useApi({ showToastOnError: false });
  const { isAuthenticated } = useAuth0();
  const { data: user, isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const enabled =
    isAuthenticated &&
    Boolean(user) &&
    requestId !== undefined &&
    (isExpert || isIntel || (isInnovator && Boolean(selectedCompanyId)));

  return useQuery<MissionAssessmentRequestSelectDto | null, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!requestId) {
        return null;
      }

      const path = isInnovator
        ? `/v1/innovator/mission-assessment-requests/${requestId}`
        : isExpert
          ? `/v1/expert/mission-assessment-requests/${requestId}`
          : isIntel
            ? `/v1/intel/mission-assessment-requests/${requestId}`
            : undefined;

      if (!path) {
        throw new Error('User role not supported for mission assessment request access');
      }

      try {
        const result = await apiGet<MissionAssessmentRequestSelectDto>({ path });
        return result || null;
      } catch {
        // Request was not found or failed - return null to indicate withdrawal
        return null;
      }
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentRequests.getMissionAssessmentRequest(requestId)
      : queryKeys.placeholders.default,
    retry: false,
  });
};

export const useExpertGetMissionAssessmentRequests = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = isAuthenticated;

  return useQuery<MissionAssessmentRequestSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<MissionAssessmentRequestsSelectDto>({
        path: '/v1/expert/mission-assessment-requests',
      });
      return result.missionAssessmentRequests;
    },
    queryKey: enabled
      ? queryKeys.missionAssessmentRequests.getMissionAssessmentRequests
      : queryKeys.placeholders.default,
  });
};

/**
 * Hook to prefetch a mission assessment request by ID.
 * Returns a function that can be called to prefetch request data for a specific ID.
 */
export const usePrefetchMissionAssessmentRequest = () => {
  const queryClient = useQueryClient();
  const { apiGet } = useApi({ showToastOnError: false });
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();

  const prefetch = useCallback(
    async (requestId?: string) => {
      if (!requestId) return;

      const path = isInnovator
        ? `/v1/innovator/mission-assessment-requests/${requestId}`
        : isExpert
          ? `/v1/expert/mission-assessment-requests/${requestId}`
          : isIntel
            ? `/v1/intel/mission-assessment-requests/${requestId}`
            : undefined;

      if (!path) return;

      await queryClient.prefetchQuery({
        queryFn: async () => {
          try {
            return await apiGet<MissionAssessmentRequestSelectDto>({ path });
          } catch {
            return null;
          }
        },
        queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequest(requestId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
    },
    [apiGet, isExpert, isInnovator, isIntel, queryClient],
  );

  return prefetch;
};

export const useCreateMissionAssessmentRequest = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: MissionAssessmentRequestSelectDto) => {
    updateTechnologyMissionAssessmentRequestsCache({
      missionAssessmentRequest: data,
      queryClient,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequests,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.missionAssessmentRequests.getLatestMissionAssessmentRequestForTechnology(data.technology.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(data.technology.id),
    });
  };

  return useMutation<MissionAssessmentRequestSelectDto, ApiErrorWrapper, MissionAssessmentRequestCreateDto>({
    mutationFn: async (createDto: MissionAssessmentRequestCreateDto) => {
      return await apiPost<MissionAssessmentRequestSelectDto>({
        path: '/v1/innovator/mission-assessment-requests',
        body: createDto,
      });
    },
    onSuccess,
  });
};

export const useDeleteMissionAssessmentRequest = ({
  missionAssessmentRequestId,
  technologyId,
}: {
  missionAssessmentRequestId: string;
  technologyId: string;
}) => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: MissionAssessmentRequestSelectDto) => {
    updateTechnologyMissionAssessmentRequestsCache({
      missionAssessmentRequest: data,
      queryClient,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequests,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.missionAssessmentRequests.getMissionAssessmentRequest(missionAssessmentRequestId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.missionAssessmentRequests.getLatestMissionAssessmentRequestForTechnology(technologyId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(technologyId),
    });
  };

  return useMutation<MissionAssessmentRequestSelectDto, ApiErrorWrapper>({
    mutationFn: async () => {
      return await apiDelete<MissionAssessmentRequestSelectDto>({
        path: `/v1/innovator/mission-assessment-requests/${missionAssessmentRequestId}`,
      });
    },
    onSuccess,
  });
};
